import React, { forwardRef, useEffect, useState } from 'react'
import { FormControlProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers'
import { Moment } from 'moment'
import * as R from 'ramda'

import DateFormat from '../../../constants/dateFormat'
import { DATE_FORMAT } from '../../../constants/defaults'
import i18n from '../../../locales/i18n'
import moment from '../../../momentLocale'
import { Field, Nil } from '../../../types'
import PuiInput from '../PuiInput'
import CalendarField, { CalendarFieldProps } from './CalendarField'
import CalendarToolbarComponent, {
  CalendarToolbarComponentProps,
} from './CalendarToolbarComponent'
import StaticCalendar, { StaticCalendarProps } from './StaticCalendar'

const useStyles = makeStyles(
  theme => ({
    popper: {
      zIndex: theme.zIndex.modal + 1,
    },
  }),
  { name: 'Calendar' },
)

export const CalendarVariants = {
  STATIC: 'static',
  INLINE: 'inline',
}

const VariantToComponentMap = {
  [CalendarVariants.STATIC]: StaticCalendar,
  [CalendarVariants.INLINE]: CalendarField,
  default: CalendarField,
}

export interface CalendarProps
  extends Partial<Omit<StaticCalendarProps, 'onChange' | 'value'>>,
  Partial<Omit<CalendarFieldProps, 'onChange' | 'value'>> {
  readonly field?: Field
  readonly format?: string
  readonly fullWidth?: boolean
  readonly margin?: FormControlProps['margin']
  readonly name?: string
  readonly onChange?: (date: string | Nil) => void
  readonly preferOnChangeProp?: boolean
  readonly showTopPickers?: boolean
  readonly utc?: boolean
  readonly value?: string | Moment
  readonly variant?: 'static' | 'inline'
}

const Calendar = forwardRef<HTMLDivElement, CalendarProps>(function Calendar(
  {
    showTopPickers = true,
    error: initialError,
    value: initialValue,
    onChange: initialOnChange,
    variant = 'inline',
    disabled,
    field,
    format: formatProp,
    fullWidth = false,
    minDate,
    maxDate,
    utc,
    placeholder: placeholderProp,
    preferOnChangeProp = false,
    renderDay: renderDayProp,
    ...rest
  },
  ref,
) {
  const classes = useStyles()
  const format = formatProp || DATE_FORMAT
  const placeholder =
    placeholderProp || i18n.t('Time:DATE_FORMAT_PLACEHOLDER.L')

  const error = R.isNil(field?.valid) ? initialError : !field?.valid
  const value = field?.value ?? initialValue
  const adjustedValue = utc ? moment(value).utc() : moment(value)
  const adjustedMinDate = minDate && moment(minDate)
  const adjustedMaxDate = maxDate && moment(maxDate)
  const onChange = preferOnChangeProp
    ? initialOnChange
    : (field?.setValue ?? initialOnChange)

  const [selectedDate, setSelectedDate] = useState(value ? adjustedValue : null)

  useEffect(() => {
    setSelectedDate(value ? adjustedValue : null)
  }, [value])

  const onDateChange = (newDate: Moment | null) => {
    if (!newDate) {
      if (onChange) {
        onChange(null)
      }
    } else if (
      !newDate.isSame(selectedDate, 'day') &&
      (!adjustedMinDate || moment(newDate).isSameOrAfter(adjustedMinDate))
    ) {
      setSelectedDate(newDate)
      if (onChange) {
        onChange(newDate.toISOString())
      }
    }
  }

  // eslint-disable-next-line
  const ToolbarComponent = ({
    variant: variantProp,
    ...otherProps
  }: CalendarToolbarComponentProps) => (
    <CalendarToolbarComponent
      showTopPickers={showTopPickers}
      variant={variantProp || variant}
      {...otherProps}
    />
  )

  const Component =
    VariantToComponentMap[variant] || VariantToComponentMap.default

  const getAriaLabel = (pickersDayProps: PickersDayProps<Moment>) =>
    pickersDayProps.day.format(DateFormat.FULL_DATE_COMMA_SHORT)

  const renderDay = (
    day: Moment,
    selectedDates: Moment[],
    pickersDayProps: PickersDayProps<Moment>,
  ) =>
    renderDayProp ? (
      renderDayProp(day, selectedDates, {
        ...pickersDayProps,
        'aria-label': getAriaLabel(pickersDayProps),
      })
    ) : (
      <PickersDay
        {...pickersDayProps}
        aria-label={getAriaLabel(pickersDayProps)}
        day={day}
        key={pickersDayProps.key}
        selected={day.isSame(selectedDates?.[0], 'day')}
      />
    )

  return (
    <PuiInput field={field}>
      <Component
        PopperProps={{
          className: classes.popper,
        }}
        // @ts-ignore
        ToolbarComponent={ToolbarComponent}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        inputFormat={format}
        margin="normal"
        maxDate={adjustedMaxDate}
        minDate={adjustedMinDate}
        placeholder={placeholder}
        ref={ref}
        renderDay={renderDay}
        showTopPickers={showTopPickers}
        value={selectedDate}
        views={['day']}
        onChange={onDateChange}
        {...rest}
      />
    </PuiInput>
  )
})

export default Calendar
